import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, HttpError } from '../api/client';
import type { CreateStoreBody, CustomFieldMapping, RenderCadence, Store } from '../api/types';
import { useAuth } from '../store/auth';
import { useStoreSlice } from '../store/storeRegistry';
import ColumnMapForm from './ColumnMapForm';
import DeleteStoreConfirm from './DeleteStoreConfirm';

const BQ_TABLE_RE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_]+\.[A-Za-z0-9_]+$/;

interface Props {
  store: Store;
  mode?: 'edit' | 'create';
  onClose: () => void;
}

export default function StoreEditDialog({ store, mode = 'edit', onClose }: Props) {
  const qc = useQueryClient();
  // Delete is super-admin only server-side (Task 4) — hide it from everyone else.
  const canDelete = useAuth((s) => s.me?.role === 'admin');
  const [name, setName] = useState(store.name);
  const [sourceType, setSourceType] = useState<'feed' | 'bq'>(store.source.type);
  const [feedUrl, setFeedUrl] = useState(store.source.feed_url ?? '');
  const [bqTable, setBqTable] = useState(store.source.bq_table ?? '');
  const [columnMap, setColumnMap] = useState<Record<string, string>>(
    store.source.bq_column_map ?? {},
  );
  const [customFields, setCustomFields] = useState<CustomFieldMapping[]>(
    store.source.bq_custom_fields ?? [],
  );
  const [disclaimer, setDisclaimer] = useState(store.default_disclaimer ?? '');
  const [renderCadence, setRenderCadence] = useState<RenderCadence>(
    store.render_cadence ?? 'daily',
  );
  const [defaultCutoutMode, setDefaultCutoutMode] = useState<'auto' | 'none'>(
    store.default_cutout_mode ?? 'auto',
  );
  // The re-render warning tracks a DIFF from the loaded store, not "was the
  // select touched" — flipping back to the original value hides it again.
  const cutoutModeChanged = defaultCutoutMode !== (store.default_cutout_mode ?? 'auto');
  const [sampleIds, setSampleIds] = useState(store.sample_product_ids.join(', '));
  const [error, setError] = useState<string | null>(null);
  const [bgFile, setBgFile] = useState<File | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);

  const [slug, setSlug] = useState(mode === 'create' ? '' : store.id);
  const [debouncedSlug, setDebouncedSlug] = useState(slug);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSlug(slug.trim()), 300);
    return () => clearTimeout(t);
  }, [slug]);

  const slugValid = /^[a-z0-9]([a-z0-9-]{0,30}[a-z0-9])?$/.test(debouncedSlug);
  const slugAvailQ = useQuery({
    queryKey: ['store-availability', debouncedSlug],
    queryFn: () =>
      api.getStore(debouncedSlug).then(() => false).catch((e) => {
        if (e instanceof HttpError && e.status === 404) return true;
        throw e;
      }),
    enabled: mode === 'create' && slugValid && debouncedSlug.length > 0,
    retry: false,
    staleTime: 5_000,
  });

  // Debounce the table input so we don't fire one schema fetch per keystroke.
  const [debouncedBqTable, setDebouncedBqTable] = useState(bqTable);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedBqTable(bqTable.trim()), 300);
    return () => clearTimeout(t);
  }, [bqTable]);

  const isWellFormed = BQ_TABLE_RE.test(debouncedBqTable);
  const schemaQ = useQuery({
    queryKey: ['bq-schema', debouncedBqTable],
    queryFn: () => api.bqSchema(debouncedBqTable),
    enabled: sourceType === 'bq' && isWellFormed,
    retry: false,
    staleTime: 60_000,
  });
  const availableColumns = useMemo(
    () => (schemaQ.data ? schemaQ.data.map((c) => c.name) : null),
    [schemaQ.data],
  );
  const schemaErrorDetail =
    schemaQ.error instanceof HttpError
      ? schemaQ.error.detail
      : schemaQ.error
      ? (schemaQ.error as Error).message
      : null;

  // Product count for the cutout-mode re-render warning — best-effort only:
  // the warning falls back to a count-free sentence when this errors (no BQ
  // access, dry-run route down, etc.) or hasn't resolved yet.
  const estimateQ = useQuery({
    queryKey: ['run-estimate', store.id],
    queryFn: () => api.runEstimate(store.id),
    enabled: mode === 'edit' && cutoutModeChanged,
    retry: false,
  });
  const nProducts = estimateQ.data?.n_products;

  const saveMut = useMutation({
    mutationFn: async (): Promise<Store | { ok: true }> => {
      const cleanedCustomFields =
        sourceType === 'bq'
          ? customFields
              .map((cf) => ({
                label: cf.label.trim(),
                bq_column: cf.bq_column.trim(),
              }))
              .filter((cf) => cf.label !== '' && cf.bq_column !== '')
          : [];

      if (mode === 'create') {
        // 1. Create the store with empty default_assets — backend seeds _default config server-side.
        const body: CreateStoreBody = {
          id: slug,
          name: name.trim() || slug,
          source: {
            type: sourceType,
            feed_url: sourceType === 'feed' ? feedUrl.trim() || null : null,
            bq_table: sourceType === 'bq' ? bqTable.trim() || null : null,
            bq_column_map:
              sourceType === 'bq' && Object.keys(columnMap).length > 0
                ? columnMap
                : null,
            bq_custom_fields: sourceType === 'bq' ? cleanedCustomFields : [],
          },
          default_assets: {},
          default_disclaimer: disclaimer.trim() || null,
          sample_product_ids: sampleIds
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
          render_cadence: renderCadence,
          default_cutout_mode: defaultCutoutMode,
          // hash_schema is NOT sent on create — the server always writes 4 for
          // new stores (access-levels wave, 2026-09-02); the edit-mode payload
          // below carries a legacy store's 3 forward via its `...store` spread.
          // feed_output is likewise omitted — the server applies its default
          // for feed sources (spec §5 D5); the Feeds page owns editing it.
        };
        const created = await api.createStore(body);

        // 2. Upload assets if the operator dropped files in the dialog.
        let bgPath: string | null = null;
        let logoPath: string | null = null;
        if (bgFile) {
          const out = await api.uploadAsset('background', bgFile, slug);
          bgPath = out.path;
        }
        if (logoFile) {
          const out = await api.uploadAsset('logo', logoFile, slug);
          logoPath = out.path;
        }

        // 3. If any asset uploaded, PUT the store again with the new paths.
        if (bgPath || logoPath) {
          const updated: Store = {
            ...created,
            default_assets: {
              ...(bgPath ? { background: bgPath } : {}),
              ...(logoPath ? { logo: logoPath } : {}),
            },
          };
          await api.updateStore(slug, updated);
          return updated;
        }
        return created;
      }

      const payload: Store = {
        ...store,
        name: name.trim() || store.id,
        source: {
          type: sourceType,
          feed_url: sourceType === 'feed' ? feedUrl.trim() || null : null,
          bq_table: sourceType === 'bq' ? bqTable.trim() || null : null,
          bq_column_map:
            sourceType === 'bq' && Object.keys(columnMap).length > 0
              ? columnMap
              : null,
          bq_custom_fields: sourceType === 'bq' ? cleanedCustomFields : [],
        },
        default_disclaimer: disclaimer.trim() || null,
        render_cadence: renderCadence,
        default_cutout_mode: defaultCutoutMode,
        sample_product_ids: sampleIds
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      };
      return api.updateStore(store.id, payload);
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['stores'] });
      qc.invalidateQueries({ queryKey: ['samples'] });
      if (
        mode === 'create' &&
        created &&
        typeof created === 'object' &&
        'id' in created
      ) {
        useStoreSlice.getState().setSelectedStoreId((created as Store).id);
      }
      onClose();
    },
    onError: (e: unknown) => {
      const detail = e instanceof HttpError ? e.detail : (e as Error).message;
      setError(detail || 'failed to save store');
    },
  });

  function validate(): string | null {
    if (mode === 'create') {
      if (!slug.trim()) return 'Store ID is required';
      if (!slugValid) return 'Store ID format is invalid';
      if (slugAvailQ.data === false) return 'Store ID already taken';
    }
    if (sourceType === 'feed') {
      if (!feedUrl.trim()) return 'feed URL is required for a feed source';
    } else {
      if (!bqTable.trim()) return 'BQ table is required for a BigQuery source';
      const required = ['id', 'image_url', 'price'];
      const missing = required.filter((r) => !(columnMap[r] ?? '').trim());
      if (missing.length) {
        return `column map missing required: ${missing.join(', ')}`;
      }
      const filledCfs = customFields.filter(
        (cf) => cf.label.trim() !== '' || cf.bq_column.trim() !== '',
      );
      const labels = filledCfs.map((cf) => cf.label.trim());
      if (labels.some((l) => l === '')) {
        return 'custom fields require both a label and a column';
      }
      if (new Set(labels).size !== labels.length) {
        return 'custom field labels must be unique';
      }
      const canonical = ['id', 'group_id', 'title', 'brand', 'price',
        'currency', 'sale_price', 'image_url', 'rating'];
      const shadowing = labels.find((l) => canonical.includes(l));
      if (shadowing) {
        return `custom field label "${shadowing}" shadows a built-in Product field`;
      }
    }
    return null;
  }

  function addCustomField() {
    setCustomFields([...customFields, { label: '', bq_column: '' }]);
  }
  function updateCustomField(idx: number, patch: Partial<CustomFieldMapping>) {
    const next = [...customFields];
    next[idx] = { ...next[idx], ...patch };
    setCustomFields(next);
  }
  function removeCustomField(idx: number) {
    setCustomFields(customFields.filter((_, i) => i !== idx));
  }

  function submit() {
    setError(null);
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    saveMut.mutate();
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ff-forest/30 backdrop-blur-sm animate-rise overflow-y-auto py-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="store-edit-dialog-title"
      data-testid="store-edit-dialog"
    >
      <div className="ff-card w-full max-w-xl p-6 space-y-5 mx-4 my-auto">
        <header>
          <p className="font-mono text-[11px] uppercase tracking-widish text-ff-mute">
            {mode === 'create' ? 'New store' : `Store · ${store.id}`}
          </p>
          <h2
            id="store-edit-dialog-title"
            className="font-display text-2xl text-ff-forest"
          >
            {mode === 'create' ? 'Create store' : 'Edit store'}
          </h2>
          <p className="text-sm text-ff-mute mt-1">
            Switch between a Google Shopping feed and a BigQuery table for live
            sample data.
          </p>
        </header>

        {mode === 'create' && (
          <div>
            <label className="ff-label" htmlFor="store-edit-slug">Store ID (slug)</label>
            <input
              id="store-edit-slug"
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="acme-outdoor"
              className="ff-input text-sm font-mono"
              data-testid="store-create-slug"
              autoComplete="off"
              spellCheck={false}
              autoFocus
            />
            <p className="mt-1.5 text-[11px] text-ff-mute font-mono">
              Lowercase alnum + dashes, 1–32 chars. <strong>Cannot be changed
              after creation.</strong>{' '}
              {slug.length === 0 && 'Required.'}
              {slug.length > 0 && !slugValid && (
                <span className="text-ff-danger">✗ invalid format</span>
              )}
              {slug.length > 0 && slugValid && slugAvailQ.isFetching && '…'}
              {slug.length > 0 && slugValid && slugAvailQ.data === true && (
                <span className="text-ff-forest">✓ available</span>
              )}
              {slug.length > 0 && slugValid && slugAvailQ.data === false && (
                <span className="text-ff-danger">✗ already taken</span>
              )}
            </p>
          </div>
        )}

        <div>
          <label className="ff-label" htmlFor="store-edit-name">
            Display name
          </label>
          <input
            id="store-edit-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="ff-input text-sm"
            data-testid="store-edit-name"
          />
        </div>

        <div>
          <label className="ff-label">Source type</label>
          <div
            role="radiogroup"
            aria-label="Source type"
            className="grid grid-cols-2 gap-1 rounded-xl border border-ff-sand/60 bg-white/40 p-1"
            data-testid="store-edit-source-type"
          >
            {(['feed', 'bq'] as const).map((t) => {
              const active = sourceType === t;
              return (
                <button
                  key={t}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setSourceType(t)}
                  className={`text-[11px] font-mono uppercase tracking-widish py-2 rounded-lg transition-colors ${
                    active
                      ? 'bg-ff-forest text-ff-ivory shadow-soft'
                      : 'text-ff-mute hover:text-ff-forest'
                  }`}
                  data-testid={`store-edit-source-${t}`}
                >
                  {t === 'feed' ? 'Google Shopping feed' : 'BigQuery'}
                </button>
              );
            })}
          </div>
        </div>

        {sourceType === 'feed' ? (
          <div data-testid="store-edit-feed-fields">
            <label className="ff-label" htmlFor="store-edit-feed-url">
              Feed URL
            </label>
            <input
              id="store-edit-feed-url"
              type="url"
              value={feedUrl}
              onChange={(e) => setFeedUrl(e.target.value)}
              placeholder="https://…/feed.xml"
              className="ff-input text-sm font-mono"
              data-testid="store-edit-feed-url"
            />
          </div>
        ) : (
          <div className="space-y-3" data-testid="store-edit-bq-fields">
            <div>
              <label className="ff-label" htmlFor="store-edit-bq-table">
                BigQuery table
              </label>
              <input
                id="store-edit-bq-table"
                type="text"
                value={bqTable}
                onChange={(e) => setBqTable(e.target.value)}
                placeholder="project.dataset.table"
                className="ff-input text-sm font-mono"
                data-testid="store-edit-bq-table"
              />
              <p
                className="mt-1.5 text-[11px] font-mono text-ff-mute"
                data-testid="store-edit-bq-schema-status"
              >
                {!debouncedBqTable
                  ? 'Enter a fully-qualified table id to load its columns.'
                  : !isWellFormed
                  ? 'Format: project.dataset.table'
                  : schemaQ.isFetching
                  ? 'Loading schema…'
                  : availableColumns
                  ? `Loaded ${availableColumns.length} column${
                      availableColumns.length === 1 ? '' : 's'
                    } from BigQuery.`
                  : schemaErrorDetail
                  ? `Schema lookup failed: ${schemaErrorDetail}. Falling back to free-text inputs.`
                  : 'Schema not yet loaded.'}
              </p>
            </div>
            <div>
              <label className="ff-label">Column map</label>
              <ColumnMapForm
                value={columnMap}
                onChange={setColumnMap}
                availableColumns={availableColumns}
              />
            </div>
            <div data-testid="store-edit-custom-fields">
              <label className="ff-label">Custom fields</label>
              <p className="text-[11px] text-ff-mute leading-relaxed mb-2">
                Map arbitrary BigQuery columns (e.g. <code className="font-mono">usp1</code>,
                <code className="font-mono"> custom_label_0</code>) to user-readable labels.
                These appear as overlay options in the editor.
              </p>
              {customFields.length > 0 && (
                <div className="space-y-2">
                  {customFields.map((cf, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-[1fr_1fr_auto] gap-2 items-start"
                      data-testid={`store-edit-custom-field-row-${idx}`}
                    >
                      <input
                        type="text"
                        value={cf.label}
                        placeholder="Label (e.g. Top USP)"
                        onChange={(e) =>
                          updateCustomField(idx, { label: e.target.value })
                        }
                        className="ff-input text-sm py-2"
                        data-testid={`store-edit-custom-field-label-${idx}`}
                      />
                      {availableColumns ? (
                        <select
                          value={cf.bq_column}
                          onChange={(e) =>
                            updateCustomField(idx, { bq_column: e.target.value })
                          }
                          className="ff-input text-sm py-2 font-mono"
                          data-testid={`store-edit-custom-field-column-${idx}`}
                        >
                          <option value="">— select column —</option>
                          {availableColumns.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                          {cf.bq_column && !availableColumns.includes(cf.bq_column) && (
                            <option value={cf.bq_column}>
                              {cf.bq_column} (not in schema)
                            </option>
                          )}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={cf.bq_column}
                          placeholder="BigQuery column"
                          onChange={(e) =>
                            updateCustomField(idx, { bq_column: e.target.value })
                          }
                          className="ff-input text-sm py-2 font-mono"
                          data-testid={`store-edit-custom-field-column-${idx}`}
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => removeCustomField(idx)}
                        aria-label={`Remove custom field ${cf.label || idx + 1}`}
                        className="ff-btn-ghost px-2 py-1 text-ff-mute hover:text-ff-danger"
                        data-testid={`store-edit-custom-field-remove-${idx}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={addCustomField}
                className="mt-2 text-sm text-ff-forest hover:underline"
                data-testid="store-edit-custom-field-add"
              >
                + Add custom field
              </button>
            </div>
            {mode === 'create' && (
              <div
                className="rounded-xl border border-ff-warning/30 bg-ff-warning-soft px-3 py-2 text-[12px] text-ff-warning"
                data-testid="store-create-bq-iam-banner"
              >
                <strong>One-time access grant required.</strong> Before live sample
                products can load, a Sales Smash operator must grant the pipeline
                read access to this BigQuery dataset
                (<code>scripts/grant-bq-source-access.sh project:dataset</code>).
              </div>
            )}
          </div>
        )}

        {mode === 'create' && (
          <div className="rounded-xl border border-dashed border-ff-forest/30 bg-ff-mint/20 p-3 space-y-2">
            <p className="font-mono text-[10px] uppercase tracking-widish text-ff-forest">
              Optional · brand assets
            </p>
            <p className="text-[11px] text-ff-mute">
              Upload now to make the store ready to design — or add them later from
              the editor's Brand assets section.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <label className="cursor-pointer rounded-lg bg-white border border-ff-sand/60 px-3 py-2 text-[11px] hover:border-ff-forest/40">
                {bgFile ? `📎 ${bgFile.name}` : '📎 Background'}
                <input
                  type="file" accept="image/jpeg,image/png" className="hidden"
                  onChange={(e) => setBgFile(e.target.files?.[0] ?? null)}
                  data-testid="store-create-bg-file"
                />
              </label>
              <label className="cursor-pointer rounded-lg bg-white border border-ff-sand/60 px-3 py-2 text-[11px] hover:border-ff-forest/40">
                {logoFile ? `📎 ${logoFile.name}` : '📎 Logo'}
                <input
                  type="file" accept="image/png,image/jpeg" className="hidden"
                  onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
                  data-testid="store-create-logo-file"
                />
              </label>
            </div>
          </div>
        )}

        <div>
          <label className="ff-label" htmlFor="store-edit-disclaimer">
            Default disclaimer (optional)
          </label>
          <textarea
            id="store-edit-disclaimer"
            value={disclaimer}
            onChange={(e) => setDisclaimer(e.target.value)}
            rows={2}
            className="ff-input text-sm resize-y min-h-[3rem]"
            data-testid="store-edit-disclaimer"
          />
        </div>

        <div>
          <label className="ff-label" htmlFor="store-edit-render-cadence">
            How often do your prices change?
          </label>
          <select
            id="store-edit-render-cadence"
            value={renderCadence}
            onChange={(e) => setRenderCadence(e.target.value as RenderCadence)}
            className="ff-input text-sm"
            data-testid="store-edit-render-cadence"
          >
            <option value="daily">About once a day — prefer daily rendering</option>
            <option value="2x-day">A couple of times a day — prefer twice-daily rendering</option>
            <option value="3x-day">Several times a day — prefer three daily runs</option>
            <option value="hourly">Constantly (repricer) — prefer hourly rendering</option>
          </select>
          <p className="mt-1.5 text-[11px] text-ff-mute">
            Records the desired freshness only; it does not currently change the
            deployed job schedule. Scheduler provisioning will be scoped separately.
          </p>
        </div>

        <div>
          <label className="ff-label" htmlFor="store-edit-cutout-mode">
            Cutout mode
          </label>
          <select
            id="store-edit-cutout-mode"
            value={defaultCutoutMode}
            onChange={(e) => setDefaultCutoutMode(e.target.value as 'auto' | 'none')}
            className="ff-input text-sm"
            data-testid="store-edit-cutout-mode"
          >
            <option value="auto">Cutout pipeline (default)</option>
            <option value="none">No cutout — designs can override</option>
          </select>
          <p className="mt-1.5 text-[11px] text-ff-mute">
            Store-wide default for designs that don't set their own cutout choice.
            A design's explicit setting always wins over this.
          </p>
          {cutoutModeChanged && (
            <p
              className="mt-1.5 rounded-xl border border-ff-warning/30 bg-ff-warning-soft px-3 py-2 text-[12px] text-ff-warning"
              data-testid="store-edit-cutout-mode-warning"
            >
              {nProducts
                ? `Changing this re-renders all ~${nProducts.toLocaleString()} products in this store once on the next run.`
                : 'Changing this re-renders every product in this store once on the next run.'}
            </p>
          )}
        </div>

        <div>
          <label className="ff-label" htmlFor="store-edit-sample-ids">
            Sample product IDs
          </label>
          <input
            id="store-edit-sample-ids"
            type="text"
            value={sampleIds}
            onChange={(e) => setSampleIds(e.target.value)}
            placeholder="402189, 278260, 324944"
            className="ff-input text-sm font-mono"
            data-testid="store-edit-sample-ids"
          />
          <p className="mt-1.5 text-[11px] text-ff-mute">
            3–5 IDs, comma-separated. These power the editor's preview tabs.
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-xl border border-ff-danger/30 bg-ff-danger-soft px-3 py-2 text-[12px] text-ff-danger"
            data-testid="store-edit-error"
          >
            {error}
          </div>
        )}

        {mode === 'edit' && canDelete && (
          <DeleteStoreConfirm
            storeId={store.id}
            onDeleted={() => {
              qc.invalidateQueries({ queryKey: ['stores'] });
              onClose();
            }}
          />
        )}

        <div className="space-y-2 pt-2">
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="ff-btn-ghost"
              data-testid="store-edit-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={saveMut.isPending}
              className="ff-btn-primary"
              data-testid="store-edit-save"
            >
              {saveMut.isPending
                ? (mode === 'create' ? 'Creating…' : 'Saving…')
                : (mode === 'create' ? 'Create store' : 'Save')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
